<!-- SEO landing page (target: "receipt scanner", ~5.4K/mo, KD ~8 — lowest-difficulty
     high-volume term in the niche and core to the product). -->
<template>
  <SeoLanding
    kicker="Receipt scanner"
    h1="Free Receipt Scanner: Snap a Photo, Get the Data"
    subtitle="Turn a phone photo of any receipt into clean, structured data — merchant, date, line items, tax, and total — as JSON or CSV in seconds. No manual typing."
    cta-label="Scan a receipt free"
    cta-to="/signup"
    :sections="sections"
    :faqs="faqs"
    closing-title="Stop typing receipts by hand"
    closing-subtitle="Snap, extract, export. Free for 5 pages a month — no credit card required."
    :related-links="relatedLinks"
  />
</template>

<script setup>
import SeoLanding from '@/components/marketing/SeoLanding.vue'
import { usePageSeo, faqJsonLd, articleJsonLd } from '@/composables/usePageSeo'

const CANONICAL = 'https://www.papersnap.io/receipt-scanner'

const sections = [
  {
    h2: 'What a receipt scanner actually does',
    paragraphs: [
      'A receipt scanner reads a photo or scan of a receipt and pulls out the fields that matter — the merchant, the date, each line item, the tax, and the total — so you get structured data instead of an image. Good ones handle the messy reality of receipts: crumpled paper, faded thermal ink, odd layouts, and phone photos taken at an angle.',
      'Papersnap does this with document AI rather than brittle templates, so it works across merchants you have never scanned before. Upload a photo or PDF, and a few seconds later you have the data as JSON or CSV, ready to drop into your accounting tool or a spreadsheet.',
    ],
  },
  {
    h2: 'Why scan receipts instead of typing them',
    bullets: [
      { label: 'Speed', text: 'a photo becomes structured data in seconds — no line-by-line data entry' },
      { label: 'Accuracy', text: 'no transcription typos on totals, dates, or tax amounts' },
      { label: 'Export anywhere', text: 'JSON for tools and APIs, CSV for spreadsheets and accounting imports' },
      { label: 'Expense-ready', text: 'merchant, date, and total are exactly the fields expense reports need' },
    ],
  },
  {
    h2: 'How to scan a receipt with Papersnap',
    bullets: [
      { label: 'Snap or upload', text: 'take a photo on your phone or drag in a PDF or image' },
      { label: 'Let it read', text: 'the AI classifies the receipt and extracts the fields automatically' },
      { label: 'Review', text: 'check the parsed fields — everything is shown next to the original' },
      { label: 'Export', text: 'download JSON or CSV, or send it straight to your inbox' },
    ],
  },
]

const faqs = [
  { q: 'Is Papersnap a free receipt scanner?', a: 'Yes — you can scan up to 5 pages a month free, with no credit card. Paid plans start at $9/month for higher volume. Every plan exports both JSON and CSV.' },
  { q: 'Can I scan a receipt from a phone photo?', a: 'Yes. Papersnap is built for phone photos, including angled shots, thermal receipts, and faded ink. Upload the photo and it extracts merchant, date, line items, tax, and total.' },
  { q: 'What data does the receipt scanner extract?', a: 'Merchant, date, individual line items, payment method, tax, and total — the fields expense reports and bookkeeping need. You get it as structured JSON or CSV.' },
  { q: 'Can I export scanned receipts to Excel or my accounting tool?', a: 'Yes. Export as CSV to open directly in Excel or Google Sheets, or as JSON to feed an API or accounting integration.' },
]

const relatedLinks = [
  { to: '/invoice-ocr', label: 'Invoice OCR' },
  { to: '/pdf-to-csv', label: 'PDF to CSV' },
  { to: '/intelligent-character-recognition', label: 'What is ICR?' },
]

usePageSeo({
  title: 'Free Receipt Scanner — Snap a Photo, Get the Data',
  description: 'A free receipt scanner that turns a phone photo into structured data — merchant, date, line items, tax, total — as JSON or CSV in seconds.',
  path: '/receipt-scanner',
  jsonLd: [
    articleJsonLd({ headline: 'Free Receipt Scanner: Snap a Photo, Get the Data', description: 'How a receipt scanner turns a phone photo into structured data, and how to use Papersnap.', url: CANONICAL, datePublished: '2026-07-08' }),
    faqJsonLd(faqs.map((f) => ({ question: f.q, answer: f.a }))),
  ],
})
</script>
